// The M1 inbox route is not available. Keep the public hook shape for the
// consumer while exposing one deterministic, honest unsupported state.
// Main's prior inbox query shape cannot be mapped to a real endpoint: the
// server has no transfer-review group/leg data source, and the remaining
// category fields needed by this screen have no backing values. Mapping a
// partial response would invent counts or correction data, so this hook stays
// explicitly unavailable until a complete contract exists.
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { metaSchema } from '../envelope';

export const confidenceLevelSchema = z.enum(['high', 'med', 'low', 'user']);
export type ConfidenceLevel = z.infer<typeof confidenceLevelSchema>;

export const inboxSuggestionSchema = z.object({
  path: z.array(z.string()),
  confidence: confidenceLevelSchema,
  source: z.enum(['user', 'rule', 'ai']),
});
export type InboxSuggestion = z.infer<typeof inboxSuggestionSchema>;

/** A single uncategorized (or business-ambiguous) transaction row. */
export const inboxUncategorizedItemSchema = z.object({
  kind: z.literal('uncategorized'),
  transaction_id: z.string(),
  merchant_display: z.string(),
  merchant_raw: z.string(),
  merchant_substituted: z.boolean(),
  account_last4: z.string().nullable(),
  posted_at: z.string(),
  pending: z.boolean(),
  amount: z.string(), // signed decimal string, per money.ts contract
  currency: z.string(),
  suggestion: inboxSuggestionSchema.nullable(),
  business_ambiguous: z.boolean(),
  business_hint: z.enum(['none', 'mixed', 'primary']).nullable(),
  note: z.string().nullable(),
});
export type InboxUncategorizedItem = z.infer<typeof inboxUncategorizedItemSchema>;

const transferLegSchema = z.object({
  account_last4: z.string().nullable(),
  amount: z.string(),
  posted_at: z.string(),
});

/** A candidate transfer pairing pending confirmation. */
export const inboxTransferReviewItemSchema = z.object({
  kind: z.literal('transfer_review'),
  group_id: z.string(),
  // Which identity event this pairing is. The server states it; the UI must
  // never infer 'transfer' for what may be a credit-card payment, because the
  // two are reported differently downstream.
  link_kind: z.enum(['transfer', 'cc_payment']),
  leg_a: transferLegSchema,
  leg_b: transferLegSchema,
});
export type InboxTransferReviewItem = z.infer<typeof inboxTransferReviewItemSchema>;

export const inboxItemSchema = z.discriminatedUnion('kind', [
  inboxUncategorizedItemSchema,
  inboxTransferReviewItemSchema,
]);
export type InboxItem = z.infer<typeof inboxItemSchema>;

export const inboxMetaSchema = metaSchema.and(
  z.object({
    uncategorized_count: z.number().nullable().optional(),
    transfer_review_count: z.number().nullable().optional(),
  }),
);
export type InboxMeta = z.infer<typeof inboxMetaSchema>;

export interface InboxItemsResult {
  items: InboxItem[];
  meta: InboxMeta | undefined;
}

export function useInboxItems(enabled = true) {
  return useQuery<InboxItemsResult>({
    queryKey: ['inbox'],
    enabled,
    retry: false,
    queryFn: async () => {
      throw new InboxUnavailableError();
    },
  });
}

export class InboxUnavailableError extends Error {
  constructor() {
    super('Inbox review is not available yet.');
    this.name = 'InboxUnavailableError';
  }
}
